import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtAt } from "@/lib/crm";
import { money } from "@/lib/pricing";
import { plural } from "@/components/crm/CustomerActions";

function Row({ title, sub, deleted, testId, onRestore }) {
  return (
    <div data-testid={testId} className="flex min-h-[56px] items-center justify-between gap-3 border-b border-black/[0.08] px-4 py-2.5 last:border-0">
      <div className="min-w-0">
        <p className="truncate text-[15px] font-medium text-black">{title}</p>
        <p className="truncate text-[13px] text-[#6D6D72]">{sub}</p>
        <p className="text-[12px] text-[#6D6D72]">Deleted {fmtAt(deleted.deleted_at)}{deleted.deleted_by ? ` by ${deleted.deleted_by}` : ""} · restorable until {fmtAt(deleted.expires_at)}</p>
      </div>
      <button data-testid={`${testId}-restore`} onClick={onRestore} className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-3 text-[15px] font-medium text-[#0062CC] hover:bg-[#007AFF]/10">
        <RotateCcw className="h-4 w-4" /> Restore
      </button>
    </div>
  );
}

export function RecentlyDeleted() {
  const [d, setD] = useState(null);
  const load = () => api.get("/recently-deleted").then((r) => setD(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  const restore = async (kind, x) => {
    try {
      const { data } = await api.post(`/recently-deleted/${kind}/${x.id}/restore`);
      toast.success(kind === "customers" ? `${data.name} restored with ${plural(data.projects, "project")} and ${plural(data.estimates, "estimate")}` : `${data.number} restored`);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const empty = d && !d.customers.length && !d.quotes.length;
  return (
    <section className="space-y-3" data-testid="recently-deleted">
      <div>
        <h2 className="text-[22px] font-bold text-black">Recently deleted</h2>
        <p className="text-[15px] text-[#6D6D72]">Deleted customers and estimates stay here for {d?.keep_days || 30} days.</p>
      </div>
      {empty && <p data-testid="recently-deleted-empty" className="rounded-[12px] bg-white px-4 py-6 text-center text-[15px] text-[#6D6D72]">Nothing deleted in the last 30 days.</p>}
      {!!d?.customers.length && (
        <div className="space-y-1.5"><p className="px-4 text-[13px] uppercase text-[#6D6D72]">Customers</p>
          <div className="overflow-hidden rounded-[12px] bg-white">
            {d.customers.map((x, i) => <Row key={x.id} testId={`deleted-customer-${i}`} title={x.name} deleted={x}
              sub={x.merged_into_name ? `Merged into ${x.merged_into_name}` : `${x.company ? `${x.company} · ` : ""}${plural(x.projects, "project")} · ${plural(x.estimates, "estimate")}`} onRestore={() => restore("customers", x)} />)}
          </div></div>
      )}
      {!!d?.quotes.length && (
        <div className="space-y-1.5"><p className="px-4 text-[13px] uppercase text-[#6D6D72]">Estimates</p>
          <div className="overflow-hidden rounded-[12px] bg-white">
            {d.quotes.map((x, i) => <Row key={x.id} testId={`deleted-quote-${i}`} title={`${x.number} · ${x.client_name}`} deleted={x}
              sub={`${x.items} lines · ${money(x.total)}`} onRestore={() => restore("quotes", x)} />)}
          </div></div>
      )}
    </section>
  );
}
